/** @format */

import styled, { css } from 'styled-components';
import theme from 'theme';
import { mix } from 'polished';
import { Status, ButtonProps } from './Button.types';
import config from 'config.json';
import Tooltip from 'components/global/Tooltip2';

const changeStatus = (status: Status) => {
  switch (status) {
    case 'success':
      return `
      background-color: ${theme.successGreen};
      &:hover {
        background-color: ${mix(0.15, 'white', theme.successGreen)};
      }
      &:active {
        background-color: ${mix(0.35, 'white', theme.successGreen)};
      }
      `;
    case 'error':
      return `
      background-color: ${theme.errorRed};
      &:hover {
        background-color: ${mix(0.15, 'white', theme.errorRed)};
      }
      &:active {
        background-color: ${mix(0.35, 'white', theme.errorRed)};
      }
      `;
    case 'warning':
      return `
      background-color: ${theme.indicatorOrange};
      &:hover {
        background-color: ${mix(0.15, 'white', theme.indicatorOrange)};
      }
      &:active {
        background-color: ${mix(0.35, 'white', theme.indicatorOrange)};
      }
      `;
    default:
      return theme.linkColor;
  }
};

const Icon = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
`;

const StyledButton = styled.button.attrs((props) => ({
  onClick: props.onClick
}))<ButtonProps>`
  && {
    font-size: 1rem;
    padding: 1.25rem 2.5rem;
    transition: background 150ms ease-in-out;
    font-weight: lighter;
    cursor: pointer;
    outline: none;
    max-height: ${({ maxHeight }) => maxHeight || 'initial'};
    max-width: ${({ maxWidth }) => maxWidth || 'initial'};
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'initial')};
    margin: ${({ margin }) => (margin ? margin : config.theme === 'tus' ? '0px 5px' : '0')};
    background-color: ${theme.linkColor};
    border: none;
    color: white;
    position: relative;

    &:hover {
      background-color: ${mix(0.15, 'white', theme.linkColor)};
    }

    &:active {
      background-color: ${mix(0.35, 'white', theme.linkColor)};
    }

    &:disabled {
      cursor: auto;
      background-color: #a6a6a6;
      color: white;
      border: none;
    }

    ${({ inverse }) =>
      inverse &&
      css`
        &&& {
          background-color: transparent;
          border: 1px solid ${theme.linkColor};
          color: ${theme.linkColor};

          &:hover {
            background-color: ${mix(0.03, 'black', 'white')};
          }

          &:active {
            background-color: ${mix(0.07, 'black', 'white')};
          }
        }
      `}

      ${({ status }) =>
        status &&
        css`
          &&& {
            ${changeStatus(status)}
          }
        `}

        ${({ color }) =>
          color &&
          css`
            &&& {
              background-color: ${color};
              &:hover {
                background-color: ${mix(0.15, 'white', color)};
              }
              &:active {
                background-color: ${mix(0.35, 'white', color)};
              }
            }
          `}

        ${
          config.theme === 'tus' &&
          css`
            &&& {
              ${Icon} {
                right: -5px;
              }
            }
          `
        }
`;

const changeIcon = (icon: string) => {
  switch (icon) {
    case 'success':
      return (
        <i
          className='fa-solid fa-circle-check fa-xl'
          style={{ color: theme.successGreen }}
        ></i>
      );
    case 'error':
      return (
        <i
          className='fa-solid fa-circle-xmark fa-xl'
          style={{ color: theme.errorRed }}
        ></i>
      );
    case 'warning':
      return (
        <i
          className='fa-solid fa-circle-exclamation fa-xl'
          style={{ color: theme.indicatorOrange }}
        ></i>
      );
    default:
      return null;
  }
};

const Wrapper = styled.div`
  display: inline-block;
  position: relative;

  & > .dualtech-tooltip-wrapper .tooltip.direction-top {
    left: 0;
    top: 0;
  }
`;

export const ButtonsGroup = styled.div<{
  justifyContent?: string;
  margin?: string;
}>`
  display: flex;
  margin: 0 5px;
  justify-content: ${(props) => props.justifyContent || 'center'};
  margin: ${({ margin }) => (margin ? margin : '0')};
`;

const Button = (props: ButtonProps) => {
  return props.tooltipMsg ? (
    <Wrapper>
      <Tooltip
        content={props.tooltipMsg}
        direction={props.tooltipDirection || 'top'}
      >
        <StyledButton {...props}>
          {props.children}
          {props.icon && <Icon>{changeIcon(props.icon)}</Icon>}
        </StyledButton>
      </Tooltip>
    </Wrapper>
  ) : (
    <StyledButton {...props}>
      {props.children}
      {props.icon && <Icon>{changeIcon(props.icon)}</Icon>}
    </StyledButton>
  );
};

export default Button;
